/**
 * Price Component
 *
 * This component is used to display the price of a product or variant in a formatted manner.
 * It handles cases where the product has a discounted price (compare at price) and shows
 * both the original and discounted prices accordingly.
 *
 * Props:
 * - product: The product object containing details about the product.
 * - currentVariant: The current variant object.
 * - discountPromo = Discount promo text which should come from the metafield promo_badge. Default is null.
 */

import { h, Fragment } from 'preact';
import { formatMoney } from 'utils/storefront/format-resource';

const Price = ({ product, currentVariant, discountPromo = null, isProductForm = false }) => {
  const { price } = window.getProductPrice(currentVariant, product.tags);

  let { compareAtPrice } = window.getProductPrice(currentVariant, product.tags);
  if (currentVariant?.compare_at_price) {
    compareAtPrice = {
      amount: currentVariant.compare_at_price,
      currencyCode: 'USD',
    };
  }

  const isBundleTag = product.tags?.find((tag) => tag?.startsWith('is_bundle::'));
  const bundleMsrpTag = product.tags?.find((tag) => tag?.startsWith('bundle_MSRP::'));
  const isBundle = isBundleTag ? isBundleTag.split('::')[1] === 'True' : !!product?.isBundleMSRP;
  const bundleMsrp = bundleMsrpTag ? bundleMsrpTag.split('::')[1] : product?.isBundleMSRP;
  const hasDiscount = parseFloat(compareAtPrice.amount) > parseFloat(price.amount);
  const ariaLabel = hasDiscount
    ? `Sale price ${formatMoney(price)}, originally ${formatMoney(compareAtPrice)}`
    : `Price ${formatMoney(price)}`;

  return (
    <div
      className="flex items-baseline gap-6"
      aria-label={ariaLabel}
    >
      {currentVariant && (
        <>
          {hasDiscount && (
            <>
              <span
                className="body-14-med text-faded-black"
                aria-hidden="true"
              >
                MSRP:{' '}
              </span>
              <span
                className={`body-14 ${isProductForm ? 'md:body-16' : ''} `}
                aria-hidden="true"
              >
                <s>{formatMoney(compareAtPrice)}</s>
              </span>
              <span className="sr-only">Original price: {formatMoney(compareAtPrice)}</span>
            </>
          )}

          {/* {isBundle && <span className="body-14-med md:body-16-med text-faded-black">MSRP: </span>} */}

          <span
            className={`body-14 ${isProductForm ? 'md:body-16' : ''} ${hasDiscount ? '!text-red' : ''} ${isBundle ? '!font-weight-bold !text-danube-blue' : ''}`}
            aria-hidden={hasDiscount ? 'true' : undefined}
          >
            {formatMoney(price)}
          </span>
          {hasDiscount && <span className="sr-only">Sale price: {formatMoney(price)}</span>}

          {isBundle && (
            <>
              <span
                className={`body-14 ${isProductForm ? 'md:body-16' : ''} `}
                aria-hidden="true"
              >
                <s>{bundleMsrp}</s>
              </span>
              <span className="sr-only">Bundle MSRP: {bundleMsrp}</span>
            </>
          )}

          {discountPromo && (
            <span
              className="leading-14 body-12-med rounded-full px-12 py-4"
              style={{
                backgroundColor: discountPromo.background_color,
                color: discountPromo.copy_color,
              }}
            >
              {discountPromo.copy}
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default Price;
